// [src/pages/Login/Login.jsx]

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "config/apiConfig";
import { clearAllAuthStorage, updateTokenAndExpiry } from 'utils/tokenManager';
import { useAuthSession } from 'hooks/useAuthSession';
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";

// CSS imports
import 'styles/Font.css';

// Image imports
import HypeHausLogo from 'assets/images/logo/logo.svg';
import checkedBox from 'assets/images/icon/box-checked-icon.svg';
import uncheckedBox from 'assets/images/icon/box-unchecked-icon.svg';

// Hooks imports (필요 없는 훅 제거 가능, 유지해도 무방)
// import usePreventZoom from "hooks/usePreventZoom";

// 로그인 페이지
function Login() {
    // 줌 방지 훅 사용 (안전장치)
    // usePreventZoom();

    const navigate = useNavigate();

    // 이미 로그인된 사용자는 홈으로 리다이렉트
    useAuthSession({ redirectIfFound: true, requireAuth: false });

    // 상태
    const [checked, setChecked] = useState(false);
    const [formData, setFormData] = useState({ id: "", password: "" });
    const [isLoading, setIsLoading] = useState(false);
    const [alertModal, setAlertModal] = useState({ isOpen: false, message: "" });

    // 포커스 상태 (UI 효과용)
    const [isIdFocused, setIsIdFocused] = useState(false);
    const [isPasswordFocused, setIsPasswordFocused] = useState(false);

    // 로그인 핸들러
    const handleLogin = async (e) => {
        const API_URL = `${API_BASE_URL}/login`;
        e.preventDefault();

        if (!formData.id || !formData.password) {
            setAlertModal({ isOpen: true, message: "아이디와 비밀번호를 모두 입력해 주세요." });
            return;
        }

        setIsLoading(true);

        let deviceInfo = { os: 'Web', model: 'Browser', uuid: '', fcmToken: '' };
        try {
            const storedInfo = sessionStorage.getItem('deviceInfo');
            if (storedInfo) {
                deviceInfo = JSON.parse(storedInfo);
            }
        } catch (e) {
            console.error("디바이스 정보 로드 실패:", e);
        }

        const payload = {
            phoneNumber: formData.id,
            password: formData.password,
            deviceId: deviceInfo.fcmToken || '',
            autoLogin: checked ? 'Y' : 'N',
        };

        try {
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(payload)
            });

            const data = await response.json();

            if (!response.ok || data.status === 'error' || (data.code && data.success !== 200)) {
                const errorMessage = data.message || '로그인 정보를 확인해주세요.';
                throw new Error(errorMessage);
            }

            const responseData = data.data || data;

            if (!responseData.atoken || !responseData.userInfo) {
                throw new Error('서버 응답에 토큰 또는 유저 정보가 부족합니다.');
            }

            updateTokenAndExpiry(
                responseData.atoken,
                responseData.rtoken,
                responseData.userInfo,
                checked,
            );

            navigate('/home');

        } catch (error) {
            console.error('Login Error:', error);
            setAlertModal({ isOpen: true, message: error.message });
            clearAllAuthStorage();
        } finally {
            setIsLoading(false);
        }
    };

    const toggleCheckbox = () => setChecked((prev) => !prev);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "id") {
            const numericValue = value.replace(/[^0-9]/g, '');
            setFormData(prevData => ({ ...prevData, [name]: numericValue }));
        } else {
            setFormData(prevData => ({ ...prevData, [name]: value }));
        }
    };

    const closeAlertModal = () => setAlertModal({ isOpen: false, message: "" });
    const handleSignUp = () => navigate('/agreement');
    const handleFindPassword = () => navigate('/findpw');

    const isIdActive = isIdFocused || formData.id.length > 0;
    const isPasswordActive = isPasswordFocused || formData.password.length > 0;

    return (
        <div style={staticStyles.loginPageContainer}>
            {/* 로고 영역 */}
            <header style={staticStyles.appHeader}>
                <img src={HypeHausLogo} alt="logo" style={{ maxWidth: '100%' }} />
            </header>

            {/* 메인 폼 영역 */}
            <main style={staticStyles.mainContent}>
                <form onSubmit={handleLogin} style={staticStyles.loginForm}>
                    <div style={staticStyles.inputGroup}>
                        <label style={staticStyles.label}>아이디(휴대폰번호)</label>
                        <input type="tel"
                            placeholder="휴대폰번호 입력"
                            style={{
                                ...staticStyles.commonInputBase,
                                ...(isIdActive && staticStyles.commonInputFocused)
                            }}
                            name="id"
                            value={formData.id}
                            onChange={handleChange}
                            inputMode={"numeric"}
                            onFocus={() => setIsIdFocused(true)}
                            onBlur={() => setIsIdFocused(false)}
                            autoComplete="username"
                        />
                    </div>
                    <div style={staticStyles.inputGroup}>
                        <label style={staticStyles.label}>비밀번호</label>
                        <input
                            type="password"
                            placeholder="비밀번호 입력"
                            style={{
                                ...staticStyles.commonInputBase,
                                ...(isPasswordActive && staticStyles.commonInputFocused)
                            }}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            onFocus={() => setIsPasswordFocused(true)}
                            onBlur={() => setIsPasswordFocused(false)}
                            autoComplete="current-password"
                        />
                        <div style={staticStyles.checkBoxField} onClick={toggleCheckbox}>
                            <img
                                src={checked ? checkedBox : uncheckedBox}
                                alt="체크박스"
                                style={staticStyles.checkboxImage}
                            />
                            <span style={staticStyles.autoLogin}>자동로그인</span>
                        </div>
                    </div>

                    <button
                        type="submit"
                        style={{
                            ...staticStyles.loginPageButton,
                            ...(isLoading && staticStyles.loginPageButtonDisabled)
                        }}
                        disabled={isLoading}
                    >
                        <span style={staticStyles.buttonLabel}>
                            {isLoading ? "로그인 중..." : "로그인"}
                        </span>
                    </button>
                </form>

                {/* 하단 링크 영역: 마진 탑 auto로 바닥에 붙임 */}
                <div style={staticStyles.userAction}>
                    <span style={staticStyles.actionButton} onClick={handleFindPassword}>비밀번호 찾기</span>
                    <div style={staticStyles.divider} />
                    <span style={staticStyles.actionButton} onClick={handleSignUp}>회원가입</span>
                </div>
            </main>

            <ConfirmModal
                isOpen={alertModal.isOpen}
                onClose={closeAlertModal}
                onConfirm={closeAlertModal}
                title="알림"
                message={alertModal.message}
                showCancel={false}
            />
        </div>
    );
}

const staticStyles = {
    // [핵심] 컨테이너 스타일 수정
    loginPageContainer: {
        width: '100%',
        // 100vh 대신 100dvh를 사용하여 모바일 브라우저 주소창 변화에 대응
        height: '100dvh',
        backgroundColor: '#0D042D',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // space-between 제거 -> flex-start로 변경하여 요소들이 제자리에 있도록 함
        justifyContent: 'flex-start',
        position: 'relative', // fixed 제거 -> 스크롤/키보드 이슈 최소화
        paddingTop: 'calc(env(safe-area-inset-top) + 10vh)', // 상단 여백 확보
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 24px)',
        paddingLeft: '24px',
        paddingRight: '24px',
        boxSizing: 'border-box',
        overflow: 'hidden', // 전체 스크롤 방지
    },
    appHeader: {
        marginBottom: '40px', // 로고 아래 고정 여백
        display: 'flex',
        justifyContent: 'center',
        flexShrink: 0, // 로고 크기 줄어들지 않게
    },
    mainContent: {
        width: '100%',
        maxWidth: '430px',
        display: 'flex',
        flexDirection: 'column',
        flex: 1, // 남은 공간 차지
        // 하단 내용이 화면 밖으로 나가지 않도록 오버플로우 제어
        overflowY: 'auto',
        scrollbarWidth: 'none',
    },
    loginForm: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    label: {
        fontFamily: '"Pretendard", sans-serif',
        fontSize: '14px',
        fontWeight: '500',
        color: '#9A9A9A',
    },
    commonInputBase: {
        height: '48px',
        width: '100%',
        backgroundColor: '#1B1931',
        border: '1px solid #1B1931',
        borderRadius: '6px',
        padding: '0 16px', // 상하 패딩 0으로 하고 라인하이트로 조절 (iOS 정렬 이슈 방지)
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '500',
        fontSize: '16px', // [중요] 16px로 설정하여 iOS 자동 확대 방지
        color: '#FCFCFA',
        boxSizing: 'border-box',
        outline: 'none',
        // 탭 하이라이트 제거
        WebkitTapHighlightColor: 'transparent',
    },
    commonInputFocused: {
        border: '1px solid #8052FF',
        backgroundColor: '#0D042D',
    },
    checkBoxField: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '8px',
        cursor: 'pointer',
    },
    checkboxImage: { width: '20px', height: '20px' },
    autoLogin: {
        fontFamily: '"Pretendard", sans-serif',
        fontSize: '14px',
        fontWeight: '500',
        color: '#FCFCFA',
    },
    loginPageButton: {
        height: '56px',
        borderRadius: '6px',
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        border: 'none',
        cursor: 'pointer',
        marginTop: '20px', // 버튼 위 여백
        flexShrink: 0,
    },
    loginPageButtonDisabled: {
        background: '#3A3A4A',
        cursor: 'not-allowed',
    },
    buttonLabel: {
        fontFamily: '"Pretendard", sans-serif',
        fontWeight: '700',
        fontSize: '16px',
        color: '#FFFFFF',
    },
    // [핵심] 하단 버튼 영역 스타일
    userAction: {
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        alignItems: 'center',
        marginTop: 'auto', // [중요] 이 속성이 하단 요소를 바닥으로 밈
        paddingTop: '20px', // 키보드 올라왔을 때 입력창과 너무 붙지 않도록
    },
    actionButton: {
        fontFamily: '"Pretendard", sans-serif',
        fontSize: '14px',
        fontWeight: '500',
        color: '#747474',
        cursor: 'pointer',
        padding: '10px', // 터치 영역 확보
    },
    divider: {
        width: '1px',
        height: '14px',
        backgroundColor: '#747474',
    },
};

export default Login;